// src/hooks/useAuth.js - Context-based authentication (shared state)
//
// All components that call useAuth() share the same user/company/loading state
// via React Context. Only ONE onAuthStateChanged listener exists for the entire app.
import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../firebase/config";
import {
  computeEffectivePermissions,
  isBillingCapable,
} from "../config/subscription";

// Public legal pages are reachable in EVERY session state. The auth handler below
// must NOT redirect or sign out a user who is simply reading these — a bare <Route>
// alone is not enough, because this handler runs globally. See PRD_LegalConsent.md C2.
const LEGAL_PATHS = ["/terms", "/privacy", "/dpa"];
const isLegalPath = (path) => LEGAL_PATHS.includes(path);

// Platform-console accounts share ONE Firebase Auth pool with customer users, so a
// platform admin can authenticate here. They carry no companyId, so there is no
// tenant to scope them to and they get signed out below — without this message the
// sign-in silently does nothing and they retry forever (BL-054).
// KEEP IN SYNC with functions/src/platform/platformExclusion.js, the canonical
// definition — functions/ and src/ cannot share a module.
const PLATFORM_ACCOUNT_MSG =
  "This email belongs to a Sahod platform account. Please use a different email.";

// Handoff for a message that must outlive a remount. `setLoading(true)` runs at the
// top of EVERY auth-state change, so PublicRoute swaps LoginPage for the loading
// screen the moment the sign-in succeeds - before the rejection below is even
// reached - and anything in LoginPage's local state is gone. sessionStorage survives
// that, and dies with the tab; LoginPage drains it on mount.
//
// Chosen over the router-state handoff used elsewhere (AddEmployee -> EmployeeList)
// because it also survives a hard reload and needs no forced navigation. The
// trade-off: only LoginPage drains it, so if the user never lands there (a second
// tab reacting to the same auth change, say) the key lingers for the tab's lifetime
// and can surface on a later visit. Narrow, and it self-clears on first read.
export const AUTH_NOTICE_KEY = "sahod.authNotice";

// Signup → VerifyEmailPage hand-off (BL-087 / E4). Signup redirects with
// window.location.href, a FULL page load, so router state cannot carry a flag.
// Deliberately its OWN key rather than reusing AUTH_NOTICE_KEY above: that slot
// is drained by LoginPage on mount, so sharing it would let a "we couldn't send
// your verification email" notice surface later inside the LOGIN error banner.
// VerifyEmailPage read-and-removes this one on mount.
export const SIGNUP_NOTICE_KEY = "sahod.signupNotice";

const AuthContext = createContext(null);

/**
 * AuthProvider — wrap this around your app (inside BrowserRouter).
 * Subscribes to onAuthStateChanged once and shares state with all useAuth() consumers.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Use a ref for pathname so the auth listener always reads the current
  // value without re-subscribing on every navigation.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      const currentPath = pathnameRef.current;

      if (firebaseUser) {
        try {
          // Read claims from JWT token (NO Firestore query needed for auth!)
          const tokenResult = await firebaseUser.getIdTokenResult();
          const { companyId, companySlug, role, permissions, userDocId, fullName, customRoleId, platformAdmin } = tokenResult.claims;

          if (companyId && companySlug) {
            // Check email verification before proceeding
            if (!firebaseUser.emailVerified) {
              setUser(null);
              setCompany(null);
              // Don't bounce an unverified user off a public legal page they're reading.
              if (currentPath !== "/verify-email" && !isLegalPath(currentPath)) {
                navigate("/verify-email", { replace: true });
              }
              setLoading(false);
              return;
            }

            // Fetch company document for additional data (name, settings, etc.)
            // This is the ONLY Firestore read required for auth
            const companyDoc = await getDoc(doc(db, "companies", companyId));

            if (companyDoc.exists()) {
              const companyData = { id: companyDoc.id, ...companyDoc.data() };

              setUser({
                ...firebaseUser,
                userData: {
                  companyId,
                  companySlug,
                  role,
                  permissions: permissions || [],
                  userDocId: userDocId || null,
                  fullName: fullName || null,
                  customRoleId: customRoleId || null,
                }
              });
              setCompany(companyData);

              // Fire-and-forget: activate pending user on first verified login
              // Also refresh JWT claims from Firestore (picks up permission changes from migrations/admin edits)
              if (firebaseUser.emailVerified) {
                const functions = getFunctions(undefined, "asia-southeast1");
                httpsCallable(functions, "activateUser")({}).catch(() => {});
                httpsCallable(functions, "setUserClaims")({ companyId, companySlug })
                  .then(() => firebaseUser.getIdToken(true))
                  .then(async () => {
                    const refreshed = await firebaseUser.getIdTokenResult(true);
                    const newPerms = refreshed.claims.permissions || [];
                    const newCustomRoleId = refreshed.claims.customRoleId || null;
                    const newRole = refreshed.claims.role;
                    const permsChanged = JSON.stringify(newPerms) !== JSON.stringify(permissions || []);
                    const customRoleChanged = newCustomRoleId !== (customRoleId || null);
                    const roleChanged = newRole !== role;
                    if (permsChanged || customRoleChanged || roleChanged) {
                      setUser(prev => prev ? {
                        ...prev,
                        userData: {
                          ...prev.userData,
                          role: newRole,
                          permissions: newPerms,
                          customRoleId: newCustomRoleId,
                        }
                      } : prev);
                    }
                  })
                  .catch(() => {});
              }

              // Smart redirect logic
              const isOnLandingPage = currentPath === "/";
              const isOnAuthPage = ["/login", "/signup"].includes(currentPath);
              const isOnCompanyPage = currentPath.startsWith("/company/");

              // Redirect authenticated users away from public pages
              if (isOnLandingPage || isOnAuthPage) {
                navigate(`/company/${companySlug}/dashboard`, { replace: true });
              }
              // Validate user is accessing their own company's routes
              else if (isOnCompanyPage) {
                const urlSlug = currentPath.split("/")[2];
                if (urlSlug !== companySlug) {
                  // Redirect to correct company dashboard
                  navigate(`/company/${companySlug}/dashboard`, { replace: true });
                }
              }
            } else {
              // Company document deleted (edge case)
              console.error("Company document not found for companyId:", companyId);
              setUser(null);
              setCompany(null);
            }
          } else {
            // User authenticated but no tenant claims
            // This can happen in three scenarios:
            // 1. User is in the middle of signup (claims not set yet) - DON'T sign out
            // 2. Stale session from before custom claims implementation - sign out
            // 3. A platform-console account signing in here - sign out WITH a reason

            const isOnSignupPage = currentPath === "/signup";
            const isOnVerifyPage = currentPath === "/verify-email";

            if (isOnSignupPage || isOnVerifyPage || isLegalPath(currentPath)) {
              // User is signing up, verifying email, or reading a public legal
              // page - don't interfere (don't sign them out).
              setUser(null);
              setCompany(null);
            } else if (platformAdmin === true && !companyId) {
              // 3. Platform-console account (BL-054): no tenant to scope them to, so
              // sign out - but say why, or the sign-in silently does nothing.
              // NOT a both-claims defense: a token carrying platformAdmin AND a full
              // companyId+companySlug never reaches here, it takes the tenant branch
              // above. Mutual exclusion is enforced server-side at claim-minting time
              // (functions/src/platform/platformExclusion.js, BL-053); this only
              // handles the tenant-less shape that lands in this else.
              // Deliberately AFTER the exemption above: a platform user reading
              // /terms must not be signed out (PRD_LegalConsent C2).
              // Stash BEFORE signing out: signOut re-enters this listener and can
              // remount LoginPage, which drains the key on mount. Writing after the
              // await would race that remount and lose the message.
              // Best-effort, and deliberately guarded - a browser with site data
              // blocked throws on ANY storage access, and this must never gate the
              // sign-out. Losing the message is recoverable; leaving a tenant-less
              // account signed in is not (it would repeat on every reload).
              try {
                sessionStorage.setItem(AUTH_NOTICE_KEY, PLATFORM_ACCOUNT_MSG);
              } catch (e) {
                // No notice this time; the sign-out below still runs.
              }
              await signOut(auth);
              setUser(null);
              setCompany(null);
            } else {
              // Stale session - sign them out for a clean state
              await signOut(auth);
              setUser(null);
              setCompany(null);
            }
          }
        } catch (error) {
          console.error("Error in auth state handler:", error);
          setUser(null);
          setCompany(null);
        }
      } else {
        // User not authenticated
        setUser(null);
        setCompany(null);

        // Redirect protected routes to login
        if (currentPath.startsWith("/company/")) {
          navigate("/login", { replace: true });
        }
      }

      setLoading(false);
    });

    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch the company doc on demand (e.g. after the subscription simulator
  // writes a new status, or after a Phase 2 renewal). The auth listener only
  // fetches the company once, so without this gating would not re-evaluate.
  const companyId = user?.userData?.companyId;
  const refreshCompany = useCallback(async () => {
    if (!companyId) return;
    try {
      const snap = await getDoc(doc(db, "companies", companyId));
      if (snap.exists()) setCompany({ id: snap.id, ...snap.data() });
    } catch (error) {
      console.error("refreshCompany failed:", error);
    }
  }, [companyId]);

  // --- Subscription read-only clamp (superuser-only) ------------------------
  // `user.userData.permissions` always holds the RAW (JWT-claim) permissions.
  // `canManageBilling` is derived off RAW (set:bill:edit survives the clamp, but
  // deriving it off raw avoids a circular dependency since it feeds the clamp).
  const rawPermissions = user?.userData?.permissions;
  const subscription = company?.subscription;
  const canManageBilling = isBillingCapable(rawPermissions);

  // Effective permissions = clamp only when an expired superuser is read-only
  // (pure helper). Memoized + new-reference-on-change so AppLayout's useMemo and
  // PermissionGuard re-render; returns the same reference when not clamped (no-op,
  // and reversibility is free). Never touches the `role` claim (Firestore
  // isCompanyAdmin keys off it, so a read-only superuser can still write the
  // subscription doc to renew).
  const effectivePermissions = useMemo(
    () => computeEffectivePermissions(rawPermissions, subscription),
    [rawPermissions, subscription]
  );

  const effectiveUser = useMemo(() => {
    if (!user) return null;
    if (effectivePermissions === user.userData?.permissions) return user; // not clamped
    return {
      ...user,
      userData: {
        ...user.userData,
        permissions: effectivePermissions,
        permissionsRaw: rawPermissions || [],
      },
    };
  }, [user, effectivePermissions, rawPermissions]);

  const hasPermission = useCallback((permission) => {
    if (!effectiveUser?.userData?.permissions) return false;
    return effectiveUser.userData.permissions.includes(permission);
  }, [effectiveUser]);

  const hasAnyPermission = useCallback((permissionList) => {
    if (!effectiveUser?.userData?.permissions) return false;
    return permissionList.some(p => effectiveUser.userData.permissions.includes(p));
  }, [effectiveUser]);

  const hasAllPermissions = useCallback((permissionList) => {
    if (!effectiveUser?.userData?.permissions) return false;
    return permissionList.every(p => effectiveUser.userData.permissions.includes(p));
  }, [effectiveUser]);

  const logout = async () => {
    try {
      await signOut(auth);
      // Use window.location for clean break from routing context
      window.location.replace("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const value = {
    user: effectiveUser,
    company,
    loading,
    logout,
    refreshCompany,
    canManageBilling,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth hook — returns shared auth state from the AuthProvider.
 * Must be used inside <AuthProvider>.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
